import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { Pill, type PillTone } from "../components/Pill";
import { ApiError } from "../lib/api";
import {
  advisorSignalsQueryOptions,
  applyAdvisorSignal,
  createRegisterFileSource,
  importRegisterFileSourceUpload,
  previewRegisterFileSource,
  previewRegisterFileSourceUpload,
  registerFileSourcesQueryOptions,
} from "./queries";
import {
  CANONICAL_FIELDS,
  type AdvisorSignal,
  type CanonicalField,
  type RegisterFileSourceItem,
} from "./types";

type Tab = "excel" | "advisor" | "conflicts";

export function BootstrapPage() {
  const [tab, setTab] = useState<Tab>("excel");
  return (
    <div className="page">
      <h1>Register bootstrap</h1>
      <p className="subtitle">
        Assemble the project register from CRM, an Excel workbook, or
        folder-advisor inferences. CRM &gt; Excel &gt; advisor; conflicts open
        a <code>register_source_conflict</code> discrepancy rather than
        silently resolving.
      </p>

      <div role="tablist" className="filters">
        <TabButton tab="excel" label="Excel import" current={tab} onSelect={setTab} />
        <TabButton tab="advisor" label="Folder advisor" current={tab} onSelect={setTab} />
        <TabButton tab="conflicts" label="Source conflicts" current={tab} onSelect={setTab} />
      </div>

      {tab === "excel" ? <ExcelImportSection /> : null}
      {tab === "advisor" ? <AdvisorQueueSection /> : null}
      {tab === "conflicts" ? <ConflictsSection /> : null}
    </div>
  );
}

function TabButton({
  tab,
  label,
  current,
  onSelect,
}: {
  tab: Tab;
  label: string;
  current: Tab;
  onSelect: (tab: Tab) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={current === tab}
      className={current === tab ? "btn" : "btn btn--ghost"}
      onClick={() => onSelect(tab)}
    >
      {label}
    </button>
  );
}

// -- Excel import — recurring file-source watcher --------------------------

const SOURCE_STATUS_TONE: Record<string, PillTone> = {
  active: "success",
  paused: "warning",
  removed: "neutral",
};

function ExcelImportSection() {
  return (
    <section>
      <UploadForm />
      <details className="panel" style={{ marginTop: "1.5rem" }}>
        <summary>Advanced: register a network share for periodic rescan</summary>
        <p className="empty-state">
          Use this when the workbook lives on a server-reachable share
          (UNC path / mounted CIFS) and you want the worker to re-hash
          it on a cadence. Browser file pickers can't return a full
          path, so this form takes the path as text instead.
        </p>
        <FileSourceForm />
      </details>
      <FileSourceTable />
    </section>
  );
}

function UploadForm() {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewHash, setPreviewHash] = useState<string | null>(null);
  const [previewSheet, setPreviewSheet] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, CanonicalField | "">>({});
  const [errorText, setErrorText] = useState<string | null>(null);
  const [importRunId, setImportRunId] = useState<string | null>(null);
  const [rowCounts, setRowCounts] = useState<Record<string, number> | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const preview = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Pick a workbook first");
      return previewRegisterFileSourceUpload(file);
    },
    onSuccess: (resp) => {
      setHeaders(resp.headers);
      setPreviewHash(resp.content_sha256);
      setPreviewSheet(resp.sheet_name);
      setMapping({});
      setErrorText(null);
      setImportRunId(null);
      setRowCounts(null);
    },
    onError: (err) => setErrorText(extractError(err)),
  });

  const importOnce = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Pick a workbook first");
      const column_mapping: Record<string, string> = {};
      for (const [raw, canonical] of Object.entries(mapping)) {
        if (canonical) column_mapping[raw] = canonical;
      }
      return importRegisterFileSourceUpload(file, column_mapping);
    },
    onSuccess: (resp) => {
      setImportRunId(resp.import_run_id);
      setRowCounts(resp.row_counts);
      setErrorText(null);
    },
    onError: (err) => setErrorText(extractError(err)),
  });

  const canPreview = file !== null && !preview.isPending;
  const canImport =
    canPreview &&
    !importOnce.isPending &&
    headers.length > 0 &&
    headers.some((h) => mapping[h]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.files?.[0] ?? null;
    setFile(next);
    // Picking a new file invalidates any prior preview / import.
    setHeaders([]);
    setPreviewHash(null);
    setPreviewSheet(null);
    setMapping({});
    setErrorText(null);
    setImportRunId(null);
    setRowCounts(null);
  }

  function reset() {
    setFile(null);
    setHeaders([]);
    setPreviewHash(null);
    setPreviewSheet(null);
    setMapping({});
    setErrorText(null);
    setImportRunId(null);
    setRowCounts(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <>
      <h2>Pick a workbook</h2>
      <p className="empty-state">
        Choose an .xlsx file from this computer or a mapped network
        drive. The file is uploaded once, parsed, and imported — no
        recurring rescan. For files the worker should re-check on a
        cadence, use the Advanced section below.
      </p>
      {/* Hide the native file input and trigger it from a styled
          button so the affordance matches the rest of the design
          system (`.btn--ghost`). */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => fileInputRef.current?.click()}
        >
          {file ? "Choose a different file" : "Choose workbook…"}
        </button>
        {file ? (
          <span className="empty-state">
            <code>{file.name}</code> — {(file.size / 1024).toFixed(1)} KB
          </span>
        ) : (
          <span className="empty-state">No file selected</span>
        )}
      </div>

      <div style={{ marginTop: "0.5rem" }}>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={!canPreview}
          onClick={() => preview.mutate()}
        >
          {preview.isPending ? "Reading…" : "Read headers"}
        </button>
        {previewHash ? (
          <span className="empty-state" style={{ marginLeft: "0.75rem" }}>
            sheet <code>{previewSheet}</code> · hash{" "}
            <code>{previewHash.slice(0, 12)}…</code>
          </span>
        ) : null}
      </div>

      {headers.length > 0 ? (
        <>
          <h2>2. Review column mapping</h2>
          <p className="empty-state">
            Map each workbook header to the canonical register field it
            represents. Headers left unmapped are skipped on import.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Maps to</th>
              </tr>
            </thead>
            <tbody>
              {headers.map((h) => (
                <tr key={h}>
                  <td>{h}</td>
                  <td>
                    <select
                      value={mapping[h] ?? ""}
                      onChange={(e) =>
                        setMapping((prev) => ({
                          ...prev,
                          [h]: e.target.value as CanonicalField | "",
                        }))
                      }
                    >
                      <option value="">— ignore —</option>
                      {CANONICAL_FIELDS.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <div style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn"
          disabled={!canImport}
          onClick={() => importOnce.mutate()}
        >
          {importOnce.isPending ? "Importing…" : "Import once"}
        </button>
        {importRunId ? (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={reset}
            style={{ marginLeft: "0.5rem" }}
          >
            Upload another
          </button>
        ) : null}
      </div>

      {errorText ? (
        <p className="alert" role="alert" style={{ marginTop: "0.75rem" }}>
          {errorText}
        </p>
      ) : null}

      {importRunId ? (
        <p className="empty-state" style={{ marginTop: "0.75rem" }}>
          Imported as run <code>{importRunId.slice(0, 8)}…</code>
          {rowCounts ? (
            <>
              {" "}— {rowCounts.accepted} accepted /{" "}
              {rowCounts.rejected} rejected of {rowCounts.total} rows.
            </>
          ) : null}
        </p>
      ) : null}
    </>
  );
}

function FileSourceForm() {
  const qc = useQueryClient();
  const [sharePath, setSharePath] = useState("");
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewHash, setPreviewHash] = useState<string | null>(null);
  const [previewSheet, setPreviewSheet] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, CanonicalField | "">>({});
  const [errorText, setErrorText] = useState<string | null>(null);
  const [createdSourceId, setCreatedSourceId] = useState<string | null>(null);
  const [createdRunId, setCreatedRunId] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: () =>
      previewRegisterFileSource({
        share_path: sharePath.trim(),
        file_name: fileName.trim(),
      }),
    onSuccess: (resp) => {
      setHeaders(resp.headers);
      setPreviewHash(resp.content_sha256);
      setPreviewSheet(resp.sheet_name);
      setMapping({});
      setErrorText(null);
      setCreatedSourceId(null);
      setCreatedRunId(null);
    },
    onError: (err) => setErrorText(extractError(err)),
  });

  const create = useMutation({
    mutationFn: () => {
      const column_mapping: Record<string, string> = {};
      for (const [raw, canonical] of Object.entries(mapping)) {
        if (canonical) column_mapping[raw] = canonical;
      }
      return createRegisterFileSource({
        share_path: sharePath.trim(),
        file_name: fileName.trim(),
        column_mapping,
      });
    },
    onSuccess: (resp) => {
      setCreatedSourceId(resp.source_id);
      setCreatedRunId(resp.initial_import_run_id);
      setErrorText(null);
      qc.invalidateQueries({
        queryKey: ["bootstrap", "register-file-sources"],
      });
    },
    onError: (err) => setErrorText(extractError(err)),
  });

  const canPreview =
    sharePath.trim().length > 0 && fileName.trim().length > 0;
  const canCreate =
    canPreview && headers.length > 0 && headers.some((h) => mapping[h]);

  return (
    <>
      <div className="filters" style={{ flexDirection: "column", alignItems: "stretch" }}>
        <label>
          Folder (share path)
          <input
            value={sharePath}
            onChange={(e) => setSharePath(e.target.value)}
            placeholder={String.raw`\\fileserver\projects`}
          />
        </label>
        <label>
          File name
          <input
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="projects.xlsx"
          />
        </label>
      </div>

      <div style={{ marginTop: "0.5rem" }}>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={!canPreview || preview.isPending}
          onClick={() => preview.mutate()}
        >
          {preview.isPending ? "Reading…" : "Read headers"}
        </button>
        {previewHash ? (
          <span className="empty-state" style={{ marginLeft: "0.75rem" }}>
            sheet <code>{previewSheet}</code> · hash{" "}
            <code>{previewHash.slice(0, 12)}…</code>
          </span>
        ) : null}
      </div>

      {headers.length > 0 ? (
        <table className="data-table" style={{ marginTop: "0.75rem" }}>
          <thead>
            <tr>
              <th>Header</th>
              <th>Maps to</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((h) => (
              <tr key={h}>
                <td>{h}</td>
                <td>
                  <select
                    value={mapping[h] ?? ""}
                    onChange={(e) =>
                      setMapping((prev) => ({
                        ...prev,
                        [h]: e.target.value as CanonicalField | "",
                      }))
                    }
                  >
                    <option value="">— ignore —</option>
                    {CANONICAL_FIELDS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn"
          disabled={!canCreate || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending
            ? "Saving…"
            : "Save and start scanning"}
        </button>
      </div>

      {errorText ? (
        <p className="alert" role="alert" style={{ marginTop: "0.75rem" }}>
          {errorText}
        </p>
      ) : null}

      {createdSourceId ? (
        <p className="empty-state" style={{ marginTop: "0.75rem" }}>
          Source registered (<code>{createdSourceId.slice(0, 8)}…</code>)
          {createdRunId ? (
            <>
              {" "}
              and first import run captured (
              <code>{createdRunId.slice(0, 8)}…</code>).
            </>
          ) : (
            <> — first scan will run on the next worker tick.</>
          )}
        </p>
      ) : null}
    </>
  );
}

function FileSourceTable() {
  const { data, isLoading, error, refetch } = useQuery(
    registerFileSourcesQueryOptions,
  );

  if (isLoading) {
    return (
      <p className="empty-state" style={{ marginTop: "1.5rem" }}>
        Loading watched sources…
      </p>
    );
  }
  if (error) {
    return (
      <p className="alert" role="alert" style={{ marginTop: "1.5rem" }}>
        Failed to load watched sources:{" "}
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  }

  const sources = data?.file_sources ?? [];
  return (
    <>
      <div className="toolbar">
        <h2>Watched sources</h2>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => refetch()}
        >
          Refresh
        </button>
      </div>
      {sources.length === 0 ? (
        <p className="empty-state">
          No sources registered yet. Use the form above to start
          watching a workbook.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Folder</th>
              <th>File</th>
              <th>Status</th>
              <th>Last hash</th>
              <th>Last scanned</th>
              <th>Last import</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <FileSourceRow key={s.id} source={s} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function FileSourceRow({ source }: { source: RegisterFileSourceItem }) {
  const tone = SOURCE_STATUS_TONE[source.status] ?? "neutral";
  return (
    <tr>
      <td>
        <code>{source.share_path}</code>
      </td>
      <td>
        <code>{source.file_name}</code>
      </td>
      <td>
        <Pill tone={tone}>{source.status}</Pill>
      </td>
      <td>
        {source.last_known_hash ? (
          <code>{source.last_known_hash.slice(0, 12)}…</code>
        ) : (
          <span className="empty-state">—</span>
        )}
      </td>
      <td>
        {source.last_scanned_at
          ? new Date(source.last_scanned_at).toLocaleString()
          : "—"}
      </td>
      <td>
        {source.last_import_run_id ? (
          <code>{source.last_import_run_id.slice(0, 8)}…</code>
        ) : (
          <span className="empty-state">—</span>
        )}
      </td>
    </tr>
  );
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string } | null;
    return body?.detail ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

// -- Folder advisor queue --------------------------------------------------

const SIGNAL_STATUS_TONE: Record<string, PillTone> = {
  proposed: "warning",
  applied: "success",
  declined: "neutral",
  superseded: "neutral",
};

function AdvisorQueueSection() {
  const { data, isLoading, error, refetch } = useQuery(advisorSignalsQueryOptions);
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: (signalId: string) => applyAdvisorSignal(signalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bootstrap", "advisor-signals"] });
      setActionError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string } | null;
        setActionError(body?.detail ?? err.message);
      } else if (err instanceof Error) {
        setActionError(err.message);
      } else {
        setActionError(String(err));
      }
    },
  });

  if (isLoading) {
    return <p className="empty-state">Loading advisor queue…</p>;
  }
  if (error) {
    return (
      <p className="alert" role="alert">
        Failed to load advisor signals:{" "}
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  }

  const signals = data?.advisor_signals ?? [];
  return (
    <section>
      <div className="toolbar">
        <h2>Advisor signals</h2>
        <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
          Refresh
        </button>
      </div>
      {actionError ? (
        <p className="alert" role="alert">
          {actionError}
        </p>
      ) : null}
      {signals.length === 0 ? (
        <p className="empty-state">No open advisor signals.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Candidate path</th>
              <th>Proposed type / stage</th>
              <th>Confidence</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {signals.map((s) => (
              <AdvisorRow
                key={s.id}
                signal={s}
                onApply={() => apply.mutate(s.id)}
                applying={apply.isPending && apply.variables === s.id}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AdvisorRow({
  signal,
  onApply,
  applying,
}: {
  signal: AdvisorSignal;
  onApply: () => void;
  applying: boolean;
}) {
  const tone = SIGNAL_STATUS_TONE[signal.status] ?? "neutral";
  return (
    <tr>
      <td>
        <code>{signal.candidate_path}</code>
      </td>
      <td>
        {signal.proposed_project_type_code} /{" "}
        {signal.proposed_lifecycle_stage_code}
      </td>
      <td>{(signal.confidence * 100).toFixed(0)}%</td>
      <td>
        <Pill tone={tone}>{signal.status}</Pill>
      </td>
      <td>
        {signal.status === "proposed" ? (
          <button
            type="button"
            className="btn"
            onClick={onApply}
            disabled={applying}
          >
            {applying ? "Applying…" : "Apply"}
          </button>
        ) : signal.resulting_project_id ? (
          <code>{signal.resulting_project_id.slice(0, 8)}…</code>
        ) : null}
      </td>
    </tr>
  );
}

// -- Conflicts -------------------------------------------------------------

function ConflictsSection() {
  return (
    <section>
      <h2>Register source conflicts</h2>
      <p className="empty-state">
        When CRM and Excel disagree about the same project, the kernel
        opens a <code>register_source_conflict</code> discrepancy with both
        values surfaced — CRM keeps the row (truth-source priority), the
        Excel value is recorded for reviewer comparison. The reviewer
        resolves the conflict by either correcting CRM or re-uploading the
        Excel workbook with the matching value; no in-place override.
      </p>
      <p className="empty-state">
        Open conflicts surface alongside other deficiencies on the{" "}
        <a href="/review">review queue</a>.
      </p>
    </section>
  );
}
